import type { Database } from '@shkola/db-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { must, useDb } from './db';

export type ConsentKind = Database['public']['Enums']['consent_kind'];
export type AchievementCategory = Database['public']['Enums']['achievement_category'];
export type AchievementLevel = Database['public']['Enums']['achievement_level'];

export const CONSENT_KINDS: ReadonlyArray<ConsentKind> = ['birthday', 'achievements', 'photo'];
export const ACHIEVEMENT_CATEGORIES: ReadonlyArray<AchievementCategory> = [
  'olympiad',
  'sport',
  'art',
  'science',
  'volunteering',
  'other',
];
export const ACHIEVEMENT_LEVELS: ReadonlyArray<AchievementLevel> = [
  'class',
  'school',
  'city',
  'region',
  'national',
  'international',
];

export const ACHIEVEMENT_ICONS: Record<AchievementCategory, string> = {
  olympiad: '🧠',
  sport: '🏅',
  art: '🎨',
  science: '🔬',
  volunteering: '💙',
  other: '⭐',
};

// ---------------------------------------------------------------------------------------------
// Consents
// ---------------------------------------------------------------------------------------------
export interface ConsentState {
  /** I (this guardian) agreed */
  mine: boolean;
  /** guardians who agreed / all guardians of the child */
  agreed: number;
  guardians: number;
}

/** The consents of one child, as seen by one of its guardians. */
export function useConsents(studentId: string | undefined, myMemberIds: ReadonlyArray<string>) {
  const db = useDb();
  return useQuery({
    queryKey: ['consents', studentId],
    enabled: studentId !== undefined,
    queryFn: async (): Promise<Record<ConsentKind, ConsentState>> => {
      const [consents, guardians] = await Promise.all([
        db
          .from('consents')
          .select('kind, guardian_member_id')
          .eq('student_member_id', studentId as string),
        db
          .from('guardianships')
          .select('guardian_member_id')
          .eq('student_member_id', studentId as string)
          .eq('status', 'active'),
      ]);
      const rows = must(consents);
      const all = must(guardians).length;
      const state = (kind: ConsentKind): ConsentState => {
        const of = rows.filter((r) => r.kind === kind);
        return {
          mine: of.some((r) => myMemberIds.includes(r.guardian_member_id)),
          agreed: of.length,
          guardians: all,
        };
      };
      return {
        birthday: state('birthday'),
        achievements: state('achievements'),
        photo: state('photo'),
      };
    },
  });
}

export function useSetConsent() {
  const db = useDb();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (a: { studentId: string; kind: ConsentKind; granted: boolean }) =>
      must(
        await db.rpc('set_consent', {
          p_student: a.studentId,
          p_kind: a.kind,
          p_granted: a.granted,
        }),
      ),
    onSettled: (_d, _e, a) => {
      void client.invalidateQueries({ queryKey: ['consents', a.studentId] });
      void client.invalidateQueries({ queryKey: ['birthdays'] });
      void client.invalidateQueries({ queryKey: ['achievement-board'] });
    },
  });
}

// ---------------------------------------------------------------------------------------------
// Achievements
// ---------------------------------------------------------------------------------------------
export interface Achievement {
  id: string;
  studentId: string;
  category: AchievementCategory;
  level: AchievementLevel;
  title: string;
  description: string | null;
  awardedOn: string;
  addedBy: string | null;
  studentName?: string;
}

/** A child's portfolio (the child, the family) or, with no child, what the staff may see. */
export function useAchievements(filter: { studentId: string } | { schoolId: string }) {
  const db = useDb();
  return useQuery({
    queryKey: ['achievements', filter],
    queryFn: async () => {
      let query = db
        .from('achievements')
        .select(
          'id, student_member_id, category, level, title, description, awarded_on, added_by_member_id, school_members!achievements_school_id_student_member_id_fkey (first_name, last_name)',
        )
        .order('awarded_on', { ascending: false })
        .limit(200);
      query =
        'studentId' in filter
          ? query.eq('student_member_id', filter.studentId)
          : query.eq('school_id', filter.schoolId);
      return must(await query).map((r): Achievement => ({
        id: r.id,
        studentId: r.student_member_id,
        category: r.category,
        level: r.level,
        title: r.title,
        description: r.description,
        awardedOn: r.awarded_on,
        addedBy: r.added_by_member_id,
        ...(r.school_members && {
          studentName: `${r.school_members.last_name} ${r.school_members.first_name}`,
        }),
      }));
    },
  });
}

export interface AchievementDraft {
  schoolId: string;
  studentId: string;
  addedBy: string;
  category: AchievementCategory;
  level: AchievementLevel;
  title: string;
  description: string;
  awardedOn: string;
}

export function useAchievementActions() {
  const db = useDb();
  const client = useQueryClient();
  const invalidate = () => {
    void client.invalidateQueries({ queryKey: ['achievements'] });
    void client.invalidateQueries({ queryKey: ['achievement-board'] });
  };
  return {
    add: useMutation({
      mutationFn: async (d: AchievementDraft) =>
        must(
          await db.from('achievements').insert({
            school_id: d.schoolId,
            student_member_id: d.studentId,
            added_by_member_id: d.addedBy,
            category: d.category,
            level: d.level,
            title: d.title.trim(),
            description: d.description.trim() || null,
            awarded_on: d.awardedOn,
          }),
        ),
      onSettled: invalidate,
    }),
    remove: useMutation({
      mutationFn: async (id: string) => must(await db.from('achievements').delete().eq('id', id)),
      onSettled: invalidate,
    }),
  };
}

export interface BoardEntry {
  id: string;
  studentId: string;
  name: string;
  classId: string;
  category: AchievementCategory;
  level: AchievementLevel;
  title: string;
  awardedOn: string;
}

/** Achievements of my classes, of children whose families agreed to the board. */
export function useAchievementBoard(schoolId: string | undefined) {
  const db = useDb();
  return useQuery({
    queryKey: ['achievement-board', schoolId],
    enabled: schoolId !== undefined,
    queryFn: async () =>
      must(await db.rpc('achievement_board', { p_school: schoolId as string })).map(
        (r): BoardEntry => ({
          id: r.id,
          studentId: r.student_member_id,
          name: `${r.first_name} ${r.last_initial}.`,
          classId: r.class_id,
          category: r.category,
          level: r.level,
          title: r.title,
          awardedOn: r.awarded_on,
        }),
      ),
  });
}

// ---------------------------------------------------------------------------------------------
// Birthdays
// ---------------------------------------------------------------------------------------------
export interface Birthday {
  memberId: string;
  name: string;
  classId: string;
  onDate: string;
}

/** Birthdays in my classes in the coming days (consenting children; never the year). */
export function useBirthdays(schoolId: string | undefined, days = 14) {
  const db = useDb();
  return useQuery({
    queryKey: ['birthdays', schoolId, days],
    enabled: schoolId !== undefined,
    staleTime: 60 * 60_000,
    queryFn: async () =>
      must(await db.rpc('class_birthdays', { p_school: schoolId as string, p_days: days })).map(
        (r): Birthday => ({
          memberId: r.member_id,
          name: `${r.first_name} ${r.last_initial}.`,
          classId: r.class_id,
          onDate: r.on_date,
        }),
      ),
  });
}

// ---------------------------------------------------------------------------------------------
// Students of a class (pickers for the staff)
// ---------------------------------------------------------------------------------------------
export function useClassStudents(classId: string | undefined) {
  const db = useDb();
  return useQuery({
    queryKey: ['class-students', classId],
    enabled: classId !== undefined && classId !== '',
    queryFn: async () =>
      must(
        await db
          .from('class_enrollments')
          .select('student_member_id, school_members (first_name, last_name)')
          .eq('class_id', classId as string),
      )
        .map((r) => ({
          id: r.student_member_id,
          name: r.school_members
            ? `${r.school_members.last_name} ${r.school_members.first_name}`
            : '',
        }))
        .sort((a, b) => a.name.localeCompare(b.name, 'uk')),
  });
}
